import type { VaultProvider, VaultPublicDataAll } from './fetchVaultsAll'
import type { GenericCurrency, GenericTokenList } from '../types/general'
import type { CounterpartyTerms, TermSheet } from '../terms/types'

/**
 * Two orthogonal axes for separating savings-like vaults from volatile ones.
 *
 * - `yieldProfile` — NAV behaviour / mechanism. `yield-bearing` = the
 *   share/asset ratio accrues monotonically (LST, staking, savings, lending
 *   supply). `volatile` = a trading / perp / leveraged strategy whose NAV
 *   fluctuates and can draw down (Hyperliquid HLP & friends; any leveraged
 *   strategy vault we explicitly flag).
 * - `denomination` — is the underlying a stablecoin. Independent of
 *   `yieldProfile`: sUSDe is `yield-bearing` + `stable`, wstETH is
 *   `yield-bearing` + `volatile`, an HLP-USDC vault is `volatile` + `stable`.
 */
export type YieldProfile = 'yield-bearing' | 'volatile'
export type Denomination = 'stable' | 'volatile'

export interface VaultClassification {
  yieldProfile: YieldProfile
  denomination: Denomination
}

/**
 * Optional cross-provider fields mixed into every per-provider vault type
 * (and `VaultLookupEntry`) and stamped by `stampVaultClassification`; absent
 * until then.
 *
 * Uniform **share price** — the provider's `convertToAssets`
 * (fluid/savings/lst/morpho/lista/silo/gearbox/euler-earn) or `pricePerShare`
 * (lagoon/yearn), so consumers read one set of fields instead of
 * `convertToAssets ?? pricePerShare`:
 *  - `sharePriceRaw` — raw `convertToAssets(10^shareDecimals)` (asset-scaled
 *    integer string). `'0'` for an empty vault.
 *  - `sharePrice` — that value formatted to underlying units (1 share → X
 *    underlying, a JS number).
 *  - `sharePriceUSD` — `sharePrice * priceUsd` (USD value of 1 share); absent
 *    when no underlying price was supplied/resolved.
 * All absent on hypercore/gmx (no share/asset ratio).
 */
export interface VaultClassificationFields {
  yieldProfile?: YieldProfile
  denomination?: Denomination
  sharePriceRaw?: string
  sharePrice?: number
  sharePriceUSD?: number
  /**
   * The **share token's own** token-list entry (hydrated from the provided
   * `tokenList` by `stampVaultClassification`). Unlike `asset` — which is the
   * *underlying* — this is what the vault/LST *actually is* (e.g. pumpBTC, with
   * its branded `logoURI` + `assetGroup`), so a UI can show the right icon.
   * Absent when the caller passed no token list, or the share token isn't in
   * it.
   */
  shareAsset?: GenericCurrency
  /**
   * Resolved branded icon URL for the vault — the single field a UI should
   * read to render an icon, so consumers don't re-derive the fallback. Set by
   * `stampVaultClassification` to the first of: the share token's own logo
   * (`shareAsset.logoURI`, branded — e.g. pumpBTC), the underlying asset's logo
   * (`asset.logoURI`), or — for GMX GM/GLV tokens that have no `asset` entry —
   * the long-leg token's logo. Absent when none of those resolve (e.g. no
   * token list passed, or a HyperCore vault whose underlying isn't hydrated).
   */
  logoURI?: string
  /**
   * Redemption model — whether a depositor can withdraw underlying cash
   * synchronously (standard ERC-4626 up to available liquidity) or must go
   * through a delay/queue/cooldown.
   *  - `sync` — morpho/lista/euler-earn/fluid/gearbox/silo/yearn and `instant`
   *    savings: instant withdraw up to the vault's cash. For these, ~zero
   *    withdrawable liquidity against non-trivial TVL is a genuine red flag
   *    (stuck capital — e.g. an allocator parked in an exploited market).
   *  - `async` — LSTs (unbonding/cooldown/off-chain), cooldown savings (sUSDe),
   *    and epoch/request vaults (lagoon/upshift). Zero *instant* liquidity is
   *    expected by design and must NOT be read as a risk.
   * Absent on hypercore/gmx (USD-denominated, no share redemption model).
   */
  redemptionType?: 'sync' | 'async'
  /**
   * The structured deal — rate, maturity, exit, fees, backing, counterparty,
   * availability and principal risk in the SAME shape a lending market uses,
   * so one consumer renders both. Stamped by `stampVaultTermSheets` right after
   * this pass; absent until then.
   *
   * Mixed in HERE rather than added to sixteen provider types by hand: this
   * interface is already inherited by every one of them, so the field arrives
   * everywhere at once and a new provider gets it for free.
   *
   * Read `termSheet.coverage` before trusting a silence — an absent block means
   * "not applicable" or "not wired" and the two are recorded separately.
   */
  termSheet?: TermSheet
}

/**
 * Providers whose vaults are volatile-by-construction. Every other supported
 * provider (lst, savings, morpho, silo, euler-earn, fluid, gearbox) is
 * yield-accrual. Hyperliquid HyperCore vaults (perp/HLP) are the volatile case
 * — relevant once a `hypercore` vault provider is wired into the public data.
 */
const VOLATILE_PROVIDERS = new Set<string>(['hypercore', 'gmx'])

/**
 * Providers whose vaults redeem **synchronously** (standard ERC-4626 cash
 * withdrawal up to available liquidity). For these, a near-zero withdrawable
 * balance against real TVL is a meaningful risk signal. LST/savings are
 * decided per-vault by their `withdrawalMode`; lagoon/upshift are async
 * (epoch/request) by construction.
 */
const SYNC_REDEMPTION_PROVIDERS = new Set<string>([
  'morpho',
  'lista',
  'euler-earn',
  'fluid',
  'gearbox',
  'silo',
  'yearn',
  // Aave Earn vaults are standard synchronous ERC-4626 wrappers over an
  // Aave v3 supply position — withdraw up to available reserve liquidity.
  'aave-earn',
])
const ASYNC_REDEMPTION_PROVIDERS = new Set<string>(['lagoon', 'upshift'])

/**
 * Classify a vault's redemption model — see
 * `VaultClassificationFields.redemptionType`. Pure.
 *
 * LSTs are always `async`: even `instant-or-queued` exits are buffer-limited
 * rather than full ERC-4626 cash withdrawals, so zero reported instant
 * liquidity is never a "stuck capital" signal. Savings follow their
 * `withdrawalMode` (`instant` with no cooldown → sync, otherwise async).
 */
const classifyRedemption = (
  provider: VaultProvider | 'hypercore' | 'gmx',
  v: { withdrawalMode?: string; withdrawalCooldownSeconds?: number },
): 'sync' | 'async' | undefined => {
  // Pendle PTs have no share-redemption model at all: exiting early is a SALE
  // on the AMM, and at maturity it is a par redemption. Neither answer fits,
  // and both mislead — `sync` invites the stuck-capital heuristic (a PT's
  // reported liquidity is pool DEPTH, a price constraint, not idle cash), and
  // `async` implies a queue that does not exist. Absent, like GMX/HyperCore.
  // Spectra PTs are the same instrument on a different AMM — same answer.
  if (provider === 'pendle' || provider === 'spectra') return undefined
  if (ASYNC_REDEMPTION_PROVIDERS.has(provider)) return 'async'
  if (SYNC_REDEMPTION_PROVIDERS.has(provider)) return 'sync'
  if (provider === 'lst') return 'async'
  if (provider === 'savings') {
    return v.withdrawalMode === 'instant' && !v.withdrawalCooldownSeconds
      ? 'sync'
      : 'async'
  }
  return 'sync'
}

/**
 * Per-vault overrides for strategy vaults that are volatile despite living
 * inside a yield provider (e.g. a leveraged Morpho vault, a Lagoon trading
 * strategy). Keyed by `${chainId}-${lowercasedAddress}`.
 *
 * Seeded from the TradingStrategy dataset (kept locally under
 * `scratch/tradingstrategy/`): vaults flagged `perp_dex_trading_vault` /
 * `proprietary_trading`. As of the May 2026 snapshot, ALL such vaults belong to
 * perp-DEX protocols (Hyperliquid, Lighter, GRVT, …) — none fall inside the
 * EVM providers we index — so this set is intentionally empty today. It is the
 * extension point: add `${chainId}-${address}` rows here when a leveraged /
 * trading strategy vault surfaces inside morpho/euler/silo/lagoon/etc.
 */
export const VOLATILE_VAULT_OVERRIDES = new Set<string>([
  // '1-0x…',  // <chain>-<address> of a known volatile strategy vault

  // Yield Basis yb-<ASSET> — 2×-leveraged Curve Cryptoswap LP positions
  // with impermanent loss cancelled, registered under the `savings`
  // provider. They live here because the share/asset ratio is NOT
  // monotonic: when rebalancing slippage outruns trading fees the share
  // price falls in asset terms, and three of these four printed a
  // negative trailing-30d rate at integration (2026-08-10), with the
  // protocol's earlier market generations negative on 23–45 % of all
  // days. Structurally the HLP case, inside a yield provider.
  '1-0x651d4b8168488fa163d85304662e8278d4c55baa', // yb-WBTC
  '1-0x722fc3640ba007c3e9867ccdb0dca59f2e2f29f9', // yb-cbBTC
  '1-0x771f7290428d830ecd41e980745c327e507823ec', // yb-tBTC
  '1-0x2b9c9f3bdceb5d8e36a4704f08a78fca53343cea', // yb-WETH

  // Saturn sUSDat — a savings vault whose assets are 96.5 % Strategy's STRC
  // preferred stock, held outright and MARKED to an oracle
  // (`totalAssets = usdatBalance + (strcBalance − unvested) × price`). Unlike
  // Yield Basis this one has a real, clockwork income leg — 50
  // `transferInRewards` distributions on a 3.11-day cadence, 12.19 % APR
  // measured over 152 days, which is what DefiLlama's 15.58 % reports — and it
  // is still not a yield-bearing row, because the mark moves further than the
  // coupon does. Over the vault's whole life: +3.73 % realized APR, −22.82 %
  // max drawdown (1.00954 on 2026-05-15 → 0.77915 on 2026-06-28), 33 % of
  // two-day samples negative. See SATURN.md.
  '1-0xd166337499e176bbc38a1fbd113ab144e5bd2df7', // sUSDat
])

/**
 * Curated stablecoin symbol set (normalised uppercase), compiled from the
 * dataset's `stablecoinish`-flagged denominations. Symbol-first so it is
 * chain-agnostic. Combined with a `contains 'USD'` fallback (virtually every
 * USD-substring token is a USD stablecoin) this covers the long tail without
 * per-chain address tables.
 */
export const STABLECOIN_SYMBOLS = new Set<string>([
  // USD majors
  'USDC',
  'USDT',
  'DAI',
  'USDBC',
  'USDB',
  'PYUSD',
  'USDS',
  'USDE',
  'SUSDE',
  'USDA',
  'SUSDS',
  // CDP / protocol USD
  'GHO',
  'CRVUSD',
  'LUSD',
  'BOLD',
  'DOLA',
  'FRAX',
  'FRXUSD',
  'SFRAX',
  'USR',
  'WSTUSR',
  'SCUSD',
  'RLUSD',
  'RUSD',
  'REUSD',
  'USD0',
  'USD1',
  'USDM',
  'USDD',
  'USDF',
  'USDX',
  'USDXL',
  'USDH',
  'USDR',
  'USDAI',
  'SUSDAI',
  'DEUSD',
  'SDEUSD',
  'LVLUSD',
  'SLVLUSD',
  'OUSD',
  'YUSD',
  'MSUSD',
  'MUSD',
  'DUSD',
  'AUSD',
  'HAI',
  'GYD',
  'BYUSD',
  'SATUSD',
  'XUSD',
  'VUSD',
  'USX',
  'UTY',
  'USH',
  'USD+',
  'SUSD',
  'USDM',
  // non-USD fiat stables
  'EURC',
  'EURE',
  'EURA',
  'EURS',
  'EURT',
  'EURCV',
  'JEUR',
  'ZCHF',
  'JPYC',
  'GBPT',
  // BCP Technologies' Tokenised GBP — cash + short-dated gilts, 1:1
  // redeemable. Drives `denomination` for Wren's wstGBP row, whose underlying
  // this is. NB `TGBP` is also TrueGBP's ticker; both are GBP stablecoins, so
  // the classification is right either way.
  'TGBP',
  'XSGD',
  'TRYB',
  // Brix iTRY — lira-pegged, backed by a regulated basket of Turkish
  // money-market funds. `stable` on this axis means PEGGED TO A CURRENCY, not
  // "holds its value in dollars": a lira peg is a promise about lira, and this
  // one pays ~37 % precisely because that is what the lira costs. Same reading
  // already applied to ZCHF / EURA / TRYB above.
  'ITRY',
  // long-tail USD stables (no `USD` substring) — verified against the
  // dataset's `stablecoinish` flag.
  'WM',
  'WXDAI',
  'USK',
  'SBOLD',
  'USG',
  'BOB',
])

/**
 * Per-(chain, UNDERLYING address) stablecoin overrides, for underlyings whose
 * SYMBOL cannot carry the fact.
 *
 * {@link isStablecoinSymbol} is symbol-keyed on purpose — it covers a long
 * tail across every chain without an address table. That breaks down when a
 * ticker is not an identity, and a one-letter ticker never is: United Stables
 * trades as `U` on BNB Chain, and so do Uranium3o8, Union, U Coin and a
 * Robinhood-tokenised Unity Software share. Adding `'U'` to the symbol set
 * would call every one of them a dollar stablecoin.
 *
 * So the exception is keyed on the thing that IS an identity. Consulted before
 * the symbol path, never after — an address here is an assertion about one
 * token on one chain, which the symbol heuristic cannot contradict.
 */
export const STABLECOIN_UNDERLYING_OVERRIDES = new Set<string>([
  // United Stables (`U`) — a USD stablecoin on BNB Chain, a Venus Core market
  // and the underlying of the vhU Liquidity Hub. Priced at $0.999 by Venus's
  // own ResilientOracle.
  '56-0xce24439f2d9c6a2289f741120fe202248b666666',
])

const normalizeSymbol = (s: string | undefined): string =>
  (s ?? '').trim().toUpperCase()

/** True when a token symbol denotes a (fiat-pegged) stablecoin. */
export const isStablecoinSymbol = (symbol: string | undefined): boolean => {
  const s = normalizeSymbol(symbol)
  if (!s) return false
  if (STABLECOIN_SYMBOLS.has(s)) return true
  // Conservative substring fallback — a symbol containing `USD` is, in
  // practice, always a USD stablecoin (USDC, crvUSD, sUSDe contains USD via
  // USDE? no — handled by the set). Excludes nothing real; avoids per-chain
  // address tables for the long tail.
  return s.includes('USD')
}

export interface ClassifyVaultInput {
  provider: VaultProvider | 'hypercore' | 'gmx'
  chainId: string | number
  address: string
  /** Underlying token symbol, when known (drives `denomination`). */
  underlyingSymbol?: string
  /**
   * Underlying token ADDRESS, when known. Consulted against
   * {@link STABLECOIN_UNDERLYING_OVERRIDES} before the symbol heuristic, for
   * the underlyings whose ticker cannot identify them.
   */
  underlyingAddress?: string
}

/**
 * Classify a vault on both axes. Pure — no I/O. `yieldProfile` is
 * provider-default with a per-vault override; `denomination` is
 * positive-ID by underlying symbol (defaults to `volatile` when unknown).
 */
export const classifyVault = (
  input: ClassifyVaultInput,
): VaultClassification => {
  const key = `${input.chainId}-${input.address.toLowerCase()}`
  const yieldProfile: YieldProfile =
    VOLATILE_VAULT_OVERRIDES.has(key) || VOLATILE_PROVIDERS.has(input.provider)
      ? 'volatile'
      : 'yield-bearing'
  const underlyingKey =
    input.underlyingAddress !== undefined
      ? `${input.chainId}-${input.underlyingAddress.toLowerCase()}`
      : undefined
  const denomination: Denomination =
    (underlyingKey !== undefined &&
      STABLECOIN_UNDERLYING_OVERRIDES.has(underlyingKey)) ||
    isStablecoinSymbol(input.underlyingSymbol)
      ? 'stable'
      : 'volatile'
  return { yieldProfile, denomination }
}

/** A vault object as seen by the stamping pass — the load-bearing subset. */
type ClassifiableVault = VaultClassificationFields & {
  address: string
  /** Share symbol — absent on HyperCore (USD-denominated, no share token). */
  symbol?: string
  asset?: { symbol?: string; logoURI?: string }
  /** GMX long-leg token address — icon fallback for GM/GLV tokens that have
   *  no `asset` entry of their own. */
  longToken?: string
  /** Underlying token address, where the provider's row carries one — lets
   *  `denomination` fall back to an identity when the ticker is not one. */
  underlying?: string
  /** Provider share-price fields — folded into the uniform `sharePrice*`. */
  convertToAssets?: string
  pricePerShare?: string
  /** Asset decimals + underlying USD price — for the formatted/USD variants. */
  decimals?: number
  assetDecimals?: number
  priceUsd?: number
  /** Withdrawal mechanism (lst/savings) — drives `redemptionType`. */
  withdrawalMode?: string
  withdrawalCooldownSeconds?: number
}

/**
 * Mutate every vault in a `VaultPublicDataAll` payload, setting
 * `yieldProfile` + `denomination` via {@link classifyVault}. Called once in
 * `getVaultPublicDataAll` after the providers resolve and before the lookup
 * is built, so both the per-provider objects and the lookup carry the fields.
 *
 * `denomination` reads the underlying token symbol (`asset.symbol`), falling
 * back to the share symbol when asset metadata wasn't hydrated — most stable
 * vaults embed the stablecoin in their share symbol (`steakUSDC`), and
 * volatile shares rarely contain a stable token name.
 */
export const stampVaultClassification = (
  data: VaultPublicDataAll,
  chainId: string | number,
  tokenList: GenericTokenList = {},
): void => {
  const stampBag = (
    bag: Record<string, ClassifiableVault> | undefined,
    provider: VaultProvider | 'hypercore' | 'gmx',
    // HyperCore/GMX are USD-denominated with no share/asset ratio: they
    // self-classify (`yieldProfile`/`denomination` set on their own types) and
    // have no share price. Pass `false` to stamp only the resolved icon for
    // them and skip `classifyVault` (which would otherwise overwrite their
    // correct denomination with `volatile`).
    classify = true,
  ) => {
    if (!bag) return
    for (const v of Object.values(bag)) {
      // The share token's own token-list entry (branded icon / assetGroup).
      const shareAsset = tokenList[v.address?.toLowerCase?.() ?? '']
      if (shareAsset) v.shareAsset = shareAsset
      // Resolved icon, in fallback order: the share token's own branded logo,
      // the underlying asset's logo, then the GMX long-leg token's logo (GM/GLV
      // tokens carry no `asset` entry). See `VaultClassificationFields.logoURI`.
      const longLogo =
        v.longToken != null
          ? tokenList[v.longToken.toLowerCase()]?.logoURI
          : undefined
      const icon = v.shareAsset?.logoURI ?? v.asset?.logoURI ?? longLogo
      if (icon) v.logoURI = icon

      if (!classify) {
        // USD-denominated basket tokens (GMX GM/GLV, HyperCore) have no 4626
        // share/asset ratio, so the 4626 `sharePrice*` fold below is skipped.
        // Their per-token USD value (`priceUsd`, from the on-chain reader) IS
        // the share price, so surface it directly as `sharePriceUSD`.
        if (v.priceUsd != null && Number.isFinite(v.priceUsd))
          v.sharePriceUSD = v.priceUsd
        continue
      }

      const c = classifyVault({
        provider,
        chainId,
        address: v.address,
        underlyingSymbol: v.asset?.symbol ?? v.symbol,
        underlyingAddress: v.underlying,
      })
      v.yieldProfile = c.yieldProfile
      v.denomination = c.denomination
      const redemption = classifyRedemption(provider, v)
      if (redemption) v.redemptionType = redemption
      // Uniform share price: raw (whichever field the provider populated),
      // plus a formatted variant and its USD value via the underlying price.
      const raw = v.convertToAssets ?? v.pricePerShare
      if (raw != null) {
        v.sharePriceRaw = raw
        // `sharePriceRaw` is asset-scaled, so format with the asset decimals
        // (`assetDecimals` when share≠asset, e.g. Lagoon; else `decimals`).
        const assetDec = v.assetDecimals ?? v.decimals
        if (assetDec != null) {
          const formatted = Number(raw) / 10 ** assetDec
          if (Number.isFinite(formatted)) {
            v.sharePrice = formatted
            if (v.priceUsd != null) v.sharePriceUSD = formatted * v.priceUsd
          }
        }
      }
    }
  }
  stampBag(data.fluid, 'fluid')
  stampBag(data.gearbox, 'gearbox')
  stampBag(data.morpho, 'morpho')
  stampBag(data.lista, 'lista')
  stampBag(data.silo, 'silo')
  stampBag(data['euler-earn'], 'euler-earn')
  stampBag(data.termmax, 'termmax')
  stampBag(data.lst, 'lst')
  stampBag(data.savings, 'savings')
  stampBag(data.lagoon, 'lagoon')
  stampBag(data['aave-earn'], 'aave-earn')
  stampBag(data.upshift, 'upshift')
  stampBag(data.yearn, 'yearn')
  // Pendle PTs classify normally (a fixed-rate bond held to maturity is
  // yield-bearing, and its denomination follows the underlying), but carry no
  // `convertToAssets`/`pricePerShare`, so the share-price fold below is a
  // no-op for them — deliberately, since a PT's market price is a discount to
  // par, not a redemption value.
  stampBag(data.pendle, 'pendle')
  stampBag(data.spectra, 'spectra')
  // Icon-only pass for the USD-denominated providers (no re-classification).
  stampBag(data.hypercore, 'hypercore', false)
  stampBag(data.gmx, 'gmx', false)
}
